// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import { Page, Text, View, Document, Image } from '@react-pdf/renderer';
import logo from 'Assets/aw-horizontal-navy.png';
import checkIcon from 'Assets/check-icon.png';
import checkCircleIcon from 'Assets/circle-check.png';
import { formatValue, deliverableCost, deliverableTypeFactor, formatCreditsAndDollars } from 'Utils/deliverables';
import { dollars, dollarsPerFoot } from 'Utils/numberFormat';
import { formatDiscountAmount, formatOfferEnds } from 'Utils/promotion';
import { licenseFeeRowLabel } from 'Utils/licenseFee';
import moment from 'moment';
import styles from './styles';

interface PDFProps {
  data: IPdf
}

export const PDF = React.memo(({ data }: PDFProps) => {
  const {
    type,
    automatePlanType,
    projectName,
    referenceId,
    team,
    submitter,
    acreage,
    kmlLength,
    middlePoint,
    coordinates,
    cadTemplate,
    crs,
    topoMajr,
    topoMinr,
    selectedBundle,
    businessDays,
    bundleDeliverables,
    selectedAdditionals,
    screenshot,
    orderDate,
    deliveryDate,
    chargedCredits,
    chargedCreditsInDollars,
    totalCreditsCharged,
    totalCreditsChargedInDollars,
    addOnsCreditsString,
    deliverablesCreditsString,
    layersCreditsString,
    totalAdditionalCharged,
    totalCostForProject,
    anticipatedKickoffDate,
    imageryProvider,
    imageryCreditCost,
    imageryDollarCost,
    salesTaxAmount,
    rasterizationCreditCost,
    rasterizationDollarCost,
    bundleType,
    pricePerFoot,
    additionalInstructions,
    amtChild,
    salesNoDollars,
    salesNoCredits,
    promotion,
    pricingOverrideSummary,
    subtotalDollar,
    contractedPriceDollar,
    discountDollar,
    extraLayerCount,
    extraLayerSurchargeDollar,
    dollarPerCredit,
    tierName,
    requiresManualQuote,
    deploymentFeeDollar,
    licenseFeeMode,
    licenseFeeDollar,
    licenseFeePercent,
    licenseFeeCadence,
    overageCredits,
    overagePerCredit,
    overageDollar,
  } = data;
  const addOnsDeliverables: IAddOn[] = [];
  const topoSegmentationDeliverables: IAddOn[] = [];
  const addOnsTopography: IAddOn[] = [];
  const layersUtilities: IAddOn[] = [];
  const layersRoadSidewalk: IAddOn[] = [];
  const layersVegetationLandscape: IAddOn[] = [];
  const layersTopography: IAddOn[] = [];
  const layersPlanimetrics: IAddOn[] = [];
  const layersWallsFences: IAddOn[] = [];
  const ugBasicAddon: IAddOn[] = [];
  const aerialMiscWires: IAddOn[] = [];
  const aerialBuiltInfrastructure: IAddOn[] = [];

  const utilityPoles: IAddOn[] = [];
  const wiresAttachmentsDeliverables: IAddOn[] = [];
  const utilityPoleEquipmentDeliverables: IAddOn[] = [];
  const guyWiresDeliverables: IAddOn[] = [];
  const clearancesDeliverables: IAddOn[] = [];
  const basemapDeliverables: IAddOn[] = [];
  const gisDataDeliverables: IAddOn[] = [];
  // Catalog products are sized in linear feet like the rest of the utilities flow, but are
  // priced from the catalog rather than by acreage, so they get their own cost breakdown.
  const catalogProduct = bundleType === 'utilities_product';
  const utilitiesBundle = bundleType === 'utilities' || catalogProduct;

  selectedAdditionals.forEach((item: IAddOn) => {
    if (item.type === 'deliverable') {
      switch (item.subtype) {
      case 'Deliverables': addOnsDeliverables.push(item); break;
      case 'Topography': addOnsTopography.push(item); break;
      case 'Topography & Segmentation': topoSegmentationDeliverables.push(item); break;
      default: break;
      }
    } else {
      switch (item.subtype) {
      case 'Utilities': layersUtilities.push(item); break;
      case 'Roads & Sidewalks': layersRoadSidewalk.push(item); break;
      case 'Vegetation & Landscape': layersVegetationLandscape.push(item); break;
      case 'Topography': layersTopography.push(item); break;
      case 'Planimetrics': layersPlanimetrics.push(item); break;
      case 'Walls & Fences': layersWallsFences.push(item); break;
      case 'Misc. Wires': aerialMiscWires.push(item); break;
      case 'Built Infrastructure': aerialBuiltInfrastructure.push(item); break;
      case 'Utility Poles': utilityPoles.push(item); break;
      case 'Utility Poles - Basic': utilityPoles.push(item); break;
      case 'Wires & Attachments': wiresAttachmentsDeliverables.push(item); break;
      case 'Utility Pole - Equipment': utilityPoleEquipmentDeliverables.push(item); break;
      case 'Guy Wire': guyWiresDeliverables.push(item); break;
      case 'Clearances': clearancesDeliverables.push(item); break;
      case 'Basemap': basemapDeliverables.push(item); break;
      case 'Available GIS Data': gisDataDeliverables.push(item); break;
      case 'UG Heavy Add-On': ugBasicAddon.push(item); break;
      case 'Underground Basic Add-Ons': ugBasicAddon.push(item); break;
      case 'UG Utility Heavy Add-on Attributions': ugBasicAddon.push(item); break;
      case 'Aerial 3D Add-on Attributions': aerialBuiltInfrastructure.push(item); break;
      case 'Additional Layers': layersTopography.push(item); break;
      default: break;
      }
    }
  });

  const addOnsSelected = addOnsDeliverables.length || topoSegmentationDeliverables.length || addOnsTopography.length;
  const layersSelected = layersUtilities.length || layersRoadSidewalk.length || layersVegetationLandscape.length || layersTopography.length ||
  layersPlanimetrics.length || layersWallsFences.length || ugBasicAddon.length || aerialMiscWires.length ||
  aerialBuiltInfrastructure.length || utilityPoles.length || wiresAttachmentsDeliverables.length || utilityPoleEquipmentDeliverables.length || guyWiresDeliverables.length || clearancesDeliverables.length || basemapDeliverables.length || gisDataDeliverables.length;

  const addOns = [
    { name: 'Deliverables', data: addOnsDeliverables },
    { name: 'Topography', data: addOnsTopography },
    { name: 'Topography & Segmentation', data: topoSegmentationDeliverables },
  ];

  const layers = [
    { name: 'Misc. Wires', data: aerialMiscWires },
    { name: 'Built Infrastructure', data: aerialBuiltInfrastructure },
    { name: 'Utilities', data: layersUtilities },
    { name: 'Roads & Sidewalks', data: layersRoadSidewalk },
    { name: 'Vegetation & Landscape', data: layersVegetationLandscape },
    { name: 'Topography', data: layersTopography },
    { name: 'Planimetrics', data: layersPlanimetrics },
    { name: 'Walls & Fences', data: layersWallsFences },
    { name: 'Underground Add-Ons', data: ugBasicAddon },
    { name: 'Utility Poles', data: utilityPoles },
    { name: 'Wires & Attachments', data: wiresAttachmentsDeliverables },
    { name: 'Utility Pole - Equipment', data: utilityPoleEquipmentDeliverables },
    { name: 'Guy Wire', data: guyWiresDeliverables },
    { name: 'Clearances', data: clearancesDeliverables },
    { name: 'Basemap', data: basemapDeliverables },
    { name: 'Available GIS Data', data: gisDataDeliverables },
  ];

  const deliverySpeed = selectedBundle.deliverySpeed?.split('_').join(' ');
  const date = moment(new Date()).format('MMMM Do YYYY, h:mm a');
  const expressAIBundle = selectedBundle.bundleId === 9 || selectedBundle.name === '2D Only - Express AI';
  const noShowCredit = salesNoCredits || amtChild;
  const noShowDollar = salesNoDollars || amtChild;

  const getItemCreditDollarText = (item: IAddOn): string => {
    const shouldShowCredits = item.acres !== undefined || type === 'Estimate';
    const effectiveSalesNoCredits = noShowCredit || !shouldShowCredits;
    const effectiveSalesNoDollar = noShowDollar || shouldShowCredits;
    const credits = deliverableCost(deliverableTypeFactor(item.typeFactor, item.is3D), acreage);
    return `   ${formatCreditsAndDollars(credits, item.cost, effectiveSalesNoCredits, effectiveSalesNoDollar)}`;
  };

  return (
    <Document>
      <Page size="LETTER">
        <View style={styles.container}>
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <Image src={logo} style={styles.logo} />
            </View>
            <View style={styles.headerRight}>
              <Text style={styles.text1}>Submitter: </Text>
              <Text style={styles.text2}>{submitter || 'N/A'}</Text>
              <Text> </Text>
              <Text style={styles.text1}>Generated on: </Text>
              <Text style={styles.text2}>{date}</Text>
            </View>
          </View>
          <View style={styles.content}>
            <View style={styles.package}>
              <Text style={styles.title1}>{type === 'Estimate' ? 'Order Estimate' : 'Order Details'}</Text>
              {type === 'Invoice' && (
                <View style={styles.dates}>
                  <View style={styles.bundleItemsContainer}>
                    <Text style={styles.text2}>SUBMITTED ON:</Text>
                    <Text style={styles.text1}>{orderDate}</Text>
                  </View>
                  <View style={styles.bundleItemsContainer}>
                    <Text style={styles.text2}>DELIVERED ON:</Text>
                    <Text style={styles.text1}>{deliveryDate}</Text>
                  </View>
                </View>
              )}
              <Text style={styles.title2}>Your Package</Text>
              <Text style={styles.text1}>
                This is your base package that is included in your plan and comes with every project.
              </Text>
              <View style={styles.bundle}>
                <Image src={checkCircleIcon} style={styles.icon2} />
                <View style={styles.bundleInfo}>
                  <Text style={styles.title3}>{`${selectedBundle.name} ${selectedBundle.includeAttribute ? '(W/ ATTRIBUTES)' : ''}`}</Text>
                  {/* A cart of several catalog products (AUT-6630 D6): one line per product under the combined name */}
                  {(selectedBundle.products?.length ?? 0) > 1 && selectedBundle.products.map((product: { _id: string; name: string }) => (
                    <Text key={product._id} style={styles.text3}>{product.name}</Text>
                  ))}
                  {selectedBundle.acreAdjustmentFactor && !amtChild && !(pricePerFoot ? noShowDollar : noShowCredit) && (
                    <Text style={styles.text3}>
                      {pricePerFoot
                        ? `Unit Cost: ${dollarsPerFoot(pricePerFoot)} ${selectedBundle.name}`
                        : `1 Credit = ${selectedBundle.acreAdjustmentFactor} ${selectedBundle.name}`}
                    </Text>
                  )}
                </View>
              </View>
              {(selectedBundle.bundleId !== 5) && (
                <View style={styles.bundleItems}>
                  <View style={styles.bundleItemsContainer}>
                    <Text style={styles.text2}>LAYERS</Text>
                    {bundleDeliverables && bundleDeliverables.length > 0 && bundleDeliverables.map((item: IAddOn) =>
                      (item?.type === 'layer' ? (
                        <View style={styles.bundleListItem} key={item.id}>
                          <Image src={checkIcon} style={styles.icon} />
                          <Text style={styles.text1}>{item.name}</Text>
                        </View>
                      ) : null))}
                  </View>
                  <View style={styles.bundleItemsContainer}>
                    <Text style={styles.text2}>DELIVERABLES</Text>
                    {bundleDeliverables && bundleDeliverables.length > 0 && bundleDeliverables.map((item: IAddOn) =>
                      (item?.type === 'deliverable' ? (
                        <View style={styles.bundleListItem} key={item.id}>
                          <Image src={checkIcon} style={styles.icon} />
                          <Text style={styles.text1}>{item.name}</Text>
                        </View>
                      ) : null))}
                  </View>
                </View>
              )}
              {addOnsSelected ? (
                <View>
                  <Text style={styles.title2}>Additional Deliverables</Text>
                  {addOns.map((addOn) => (addOn.data.length ? (
                    <View key={addOn.name}>
                      <Text style={styles.text2}>{addOn.name}</Text>
                      {addOn.data.map((item) => (
                        <View style={styles.listItem} key={item.id}>
                          <View style={styles.addOnListItem}>
                            <Image src={checkIcon} style={styles.icon} />
                            <Text style={styles.text4}>
                              {item.name}
                              {getItemCreditDollarText(item)}
                            </Text>
                          </View>
                        </View>
                      ))}
                    </View>
                  ) : null))}
                </View>
              ) : null}

              {layersSelected ? (
                <View>
                  <Text style={styles.title2}>Layers</Text>
                  {layers.map((layer) => (layer.data.length ? (
                    <View key={layer.name}>
                      <Text style={styles.text2}>{layer.name}</Text>
                      {layer.data.map((item) => (
                        <View style={styles.listItem} key={item.id}>
                          <View style={styles.addOnListItem}>
                            <Image src={checkIcon} style={styles.icon} />
                            <Text style={styles.text4}>
                              {item.name}
                              {getItemCreditDollarText(item)}
                            </Text>
                          </View>
                        </View>
                      ))}
                    </View>
                  ) : null))}
                </View>
              ) : null}

              {additionalInstructions && (
                <View>
                  <Text style={styles.title2}>Additional Instructions</Text>
                  <Text style={styles.text1}>{additionalInstructions}</Text>
                </View>
              )}
            </View>

            <View style={styles.mapColumn}>
              <View style={styles.projectInfo}>
                <View style={styles.projectInfoLeft}>
                  <Text style={styles.title1}>{projectName}</Text>
                  <Text style={styles.text2}>REFERENCE ID:</Text>
                  <Text style={styles.text1}>{referenceId || 'N/A'}</Text>
                  <Text style={styles.text2}>TEAM:</Text>
                  <Text style={styles.text1}>{team || 'N/A'}</Text>
                  <Text style={styles.text1}> </Text>
                  {utilitiesBundle ? (
                    <Text style={styles.text1}>
                      {kmlLength}
                      {' '}
                      mi
                    </Text>
                  ) : (
                    <Text style={styles.text1}>
                      {acreage}
                      {' '}
                      acres
                    </Text>
                  )}
                  <Text style={styles.text1}>{middlePoint}</Text>
                  <Text style={styles.text1}>{coordinates}</Text>
                </View>
                {type === 'Estimate' && !!screenshot && (
                  <View style={styles.projectInfoRight}>
                    <Image style={styles.screenshot} src={screenshot} />
                  </View>
                )}
              </View>
              <View style={styles.line} />
              {!expressAIBundle && (
                <>
                  <Text style={styles.text2}>CAD TEMPLATE</Text>
                  <Text style={styles.text1}>{cadTemplate}</Text>
                </>
              )}
              <Text style={styles.text6}>CRS COORDINATE SYSTEM</Text>
              <Text style={styles.text1}>{crs || 'N/A'}</Text>
              {!expressAIBundle && (topoMajr || topoMinr) && (
                <View>
                  <Text style={styles.text6}>TOPOGRAPHY INTERVAL</Text>
                  <Text style={styles.text1}>
                    {topoMajr && `Major: ${topoMajr} ft`}
                    {' '}
                    {topoMinr && `Minor: ${topoMinr} ft`}
                  </Text>
                </View>
              )}
              {!expressAIBundle && (
                <>
                  <Text style={styles.text6}>PROJECT DENSITY</Text>
                  <Text style={styles.text1Cap}>{selectedBundle.density}</Text>
                  <View style={styles.line} />
                </>
              )}
              {type === 'Estimate' && !utilitiesBundle && (
                <>
                  <Text style={styles.text2}>ESTIMATED DELIVERY DATE</Text>
                  {expressAIBundle ?
                    (<Text style={styles.text3}>24 hours from order submission</Text>) :
                    (<Text style={styles.text3}>{businessDays > 20 ? 'Estimate available upon request' : `${businessDays} Business Days`}</Text>)}
                </>
              )}
              {!expressAIBundle && selectedBundle.deliverySpeed !== 'normal' && (
                <View style={styles.bundleListItem}>
                  <Image src={checkIcon} style={styles.icon} />
                  <Text style={styles.text4Cap}>
                    {deliverySpeed}
                    {' '}
                    Delivery
                  </Text>
                </View>
              )}
              {type === 'Estimate' && anticipatedKickoffDate && (
                <>
                  <Text style={styles.text2}>KICKOFF DATE</Text>
                  <Text style={styles.text3}>{moment.utc(anticipatedKickoffDate).format('MMMM Do YYYY')}</Text>
                </>
              )}
              <View style={styles.line} />

              <View style={styles.listItem}>
                <Text style={styles.text2}>{`${selectedBundle.name} ${noShowCredit ? '' : 'Credits'}`}</Text>
                {!amtChild && (
                  <Text style={styles.text2}>
                    {formatCreditsAndDollars(chargedCredits, chargedCreditsInDollars, noShowCredit, noShowDollar || expressAIBundle || type !== 'Estimate')}
                  </Text>
                )}
              </View>
              <View style={styles.orderInfo}>
                <Text style={styles.text5}>- Project Size</Text>
                {utilitiesBundle ? (
                  <Text style={styles.text5}>
                    {kmlLength}
                    {' '}
                    mi
                  </Text>
                ) : (
                  <Text style={styles.text5}>
                    {acreage}
                    {' '}
                    ac
                  </Text>
                )}
              </View>
              <View style={styles.orderInfo}>
                <Text style={styles.text5}>- Bundle</Text>
                <Text style={styles.text5}>{selectedBundle.name}</Text>
              </View>
              {!expressAIBundle && (
                <View style={styles.orderInfo}>
                  <Text style={styles.text5}>- Density</Text>
                  <Text style={styles.text5Cap}>{selectedBundle.density}</Text>
                </View>
              )}
              {!expressAIBundle && (
                <View style={styles.orderInfo}>
                  <Text style={styles.text5}>- Delivery Speed</Text>
                  <Text style={styles.text5Cap}>{deliverySpeed}</Text>
                </View>
              )}

              <View style={styles.line} />

              {!amtChild && (
                <>
                  {/* Catalog products are quoted as a fixed product price, so the PDF mirrors
                      the order form's breakdown rather than the per-add-on credit costing. */}
                  {catalogProduct && (
                    <>
                      {/* The dollar breakdown hides under salesNoDollars, same as the server
                          payload; the manual-quote note is neutral copy and always shows. */}
                      {!noShowDollar && (
                        <>
                          <View style={styles.listItem}>
                            <Text style={styles.text2}>Product Subtotal</Text>
                            <Text style={styles.text2}>{`$${formatValue(subtotalDollar ?? 0)}`}</Text>
                          </View>
                          {discountDollar > 0 && (
                            <View style={styles.listItem}>
                              <Text style={styles.text2}>{`${tierName ? `${tierName} Tier` : ''} Discount`.trim()}</Text>
                              <Text style={styles.text2}>{`-$${formatValue(discountDollar)}`}</Text>
                            </View>
                          )}
                          {extraLayerSurchargeDollar > 0 && (
                            <View style={styles.listItem}>
                              <Text style={styles.text2}>{`Additional Layers Surcharge (${extraLayerCount})`}</Text>
                              <Text style={styles.text2}>{`$${formatValue(extraLayerSurchargeDollar)}`}</Text>
                            </View>
                          )}
                          {deploymentFeeDollar > 0 && (
                            <View style={styles.listItem}>
                              <Text style={styles.text2}>Minimum project fee</Text>
                              <Text style={styles.text2}>{`+$${formatValue(deploymentFeeDollar)}`}</Text>
                            </View>
                          )}
                          {/* a pricing override's negotiated figure (AUT-6618 D8), beneath the list figures it replaces */}
                          {typeof contractedPriceDollar === 'number' && (
                            <View style={styles.listItem}>
                              <Text style={styles.text2}>Contracted price</Text>
                              <Text style={styles.text2}>{dollars(contractedPriceDollar)}</Text>
                            </View>
                          )}
                        </>
                      )}
                      {requiresManualQuote && (
                        <View style={styles.orderInfo}>
                          <Text style={styles.text5}>
                            A selection on this order requires a custom quote. A member of the AirWorks team will confirm pricing before processing.
                          </Text>
                        </View>
                      )}
                    </>
                  )}
                  {!expressAIBundle && !catalogProduct && (
                    <>
                      <View style={styles.listItem}>
                        <Text style={styles.text2}>Add-Ons</Text>
                        <Text style={styles.text2}>
                          {addOnsCreditsString}
                        </Text>
                      </View>
                      <View style={styles.orderInfo}>
                        <Text style={styles.text5}>- Deliverables</Text>
                        <Text style={styles.text5}>{deliverablesCreditsString}</Text>
                      </View>
                      <View style={styles.orderInfo}>
                        <Text style={styles.text5}>- Layers</Text>
                        <Text style={styles.text5}>{layersCreditsString}</Text>
                      </View>
                      {/* TODO - fix imageryDollarCost for order history pdf - Ishan 12/02/22 */}
                      {imageryProvider && (
                        <View style={styles.orderInfo}>
                          <Text style={styles.text5}>{`- ${imageryProvider} Imagery`}</Text>
                          <Text style={styles.text5}>
                            {formatCreditsAndDollars(imageryCreditCost, imageryDollarCost, noShowCredit, noShowDollar || imageryDollarCost === undefined)}
                          </Text>
                        </View>
                      )}
                      {rasterizationCreditCost > 0 && (
                        <View style={styles.orderInfo}>
                          <Text style={styles.text5}>- Point Cloud Rasterization</Text>
                          <Text style={styles.text5}>
                            {formatCreditsAndDollars(rasterizationCreditCost, rasterizationDollarCost, noShowCredit, noShowDollar || rasterizationDollarCost === undefined)}
                          </Text>
                        </View>
                      )}
                    </>
                  )}
                  {promotion && !promotion.supersededBy && (
                    <>
                      <View style={styles.listItem}>
                        <Text style={styles.text2}>{promotion.displayText}</Text>
                        {!noShowDollar && typeof promotion.amount === 'number' && (
                          <Text style={styles.text2}>{formatDiscountAmount(promotion.amount)}</Text>
                        )}
                      </View>
                      {type === 'Estimate' && promotion.endDate && promotion.endTimezone && (
                        <Text style={styles.text7}>{formatOfferEnds(promotion.endDate, promotion.endTimezone)}</Text>
                      )}
                    </>
                  )}
                  {(!noShowCredit) && (
                    <View style={styles.listItem}>
                      <Text style={styles.text2}>
                        {dollarPerCredit && !noShowDollar ? `Credits (${dollars(dollarPerCredit)}/credit)` : 'Total Credits Charged'}
                      </Text>
                      <Text style={styles.text2}>
                        {formatCreditsAndDollars(totalCreditsCharged, totalCreditsChargedInDollars, noShowCredit, noShowDollar || expressAIBundle || type !== 'Estimate')}
                      </Text>
                    </View>
                  )}
                  {/* The credits beyond the balance and their rate (AUT-6571): informational — the credits line
                      above already prices them, so the lines still add up to the total */}
                  {!noShowDollar && type === 'Estimate' && overageCredits > 0 && (
                    <View style={styles.listItem}>
                      <Text style={styles.text2}>{`Beyond balance: ${formatValue(overageCredits)} credits at $${formatValue(overagePerCredit)}/credit`}</Text>
                      <Text style={styles.text2}>{`$${formatValue(overageDollar)}`}</Text>
                    </View>
                  )}
                  {/* The license fee (AUT-6569): an added fee is inside the total; a prepaid license shows the
                      amount it covered outside it, so the lines above still add up to the total */}
                  {!noShowDollar && licenseFeeMode === 'added' && licenseFeeDollar > 0 && (
                    <View style={styles.listItem}>
                      <Text style={styles.text2}>{licenseFeeRowLabel('added', licenseFeePercent)}</Text>
                      <Text style={styles.text2}>{`+$${formatValue(licenseFeeDollar)}`}</Text>
                    </View>
                  )}
                  {!noShowDollar && licenseFeeMode === 'prepaid' && (
                    <View style={styles.listItem}>
                      <Text style={styles.text2}>{licenseFeeRowLabel('prepaid', licenseFeePercent, licenseFeeCadence)}</Text>
                      <Text style={styles.text2}>{`$${formatValue(licenseFeeDollar || 0)} covered`}</Text>
                    </View>
                  )}
                  {!expressAIBundle && salesTaxAmount > 0 && (
                    <View style={styles.listItem}>
                      <Text style={styles.text2}>Sales Tax</Text>
                      <Text style={styles.text2}>{`$${formatValue(salesTaxAmount)}`}</Text>
                    </View>
                  )}
                  {/* AEC only: a catalog order never draws on the balance, so nothing is "additional" to it (AUT-6630 D3) */}
                  {totalAdditionalCharged > 0 && !catalogProduct && (
                    <View style={styles.listItem}>
                      <Text style={styles.text2}>Total Additional Charges</Text>
                      <Text style={styles.text2}>
                        {`$${formatValue(totalAdditionalCharged)}`}
                      </Text>
                    </View>
                  )}
                  {(bundleType === 'utilities' || pricePerFoot) && !noShowDollar && (
                    <View style={styles.listItem}>
                      <Text style={styles.title4}>Project Price Per foot</Text>
                      <Text style={styles.title4}>
                        {pricePerFoot ? dollarsPerFoot(pricePerFoot) : 'N/A'}
                      </Text>
                    </View>
                  )}
                  {automatePlanType !== 'plan_free' && !noShowDollar && (
                    <View style={styles.listItem}>
                      <Text style={styles.title4}>Total cost for project</Text>
                      <Text style={styles.title4}>{totalCostForProject >= 0 ? `$${formatValue(totalCostForProject)}` : 'N/A'}</Text>
                    </View>
                  )}

                  {pricingOverrideSummary && (
                    <Text style={styles.text7}>
                      {`* ${pricingOverrideSummary}`}
                    </Text>
                  )}

                  {!expressAIBundle && totalAdditionalCharged === 0 && !noShowCredit && !noShowDollar && (
                    <Text style={styles.text7}>
                      &#42;
                      This project incurs no new charges. Cost is for estimating purposes and will come out of your account's current credit balance.
                    </Text>
                  )}

                  {!expressAIBundle && !catalogProduct && automatePlanType !== 'plan_free' && totalAdditionalCharged > 0 && !noShowCredit && !noShowDollar && (
                    <Text style={styles.text7}>
                      &#42; Total cost is for estimating purpose only, this project includes purchases that exceed your license contact your sales rep to upgrade and reduce your project cost.
                    </Text>
                  )}
                </>
              )}
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
});

export default PDF;
